import { BsdsInput } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { BsdsInputEvent } from '../../types/componentType';
import type { ZipCodeInputProps } from '../../types/formType';
import { formatZipCode, validateZipCode } from '../../utils/zipCodeUtils';

export const ZipCodeInput: React.FC<ZipCodeInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = '00000-000',
  required = false,
  disabled = false,
  className = '',
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
  'aria-owns': ariaOwns,
  'aria-activedescendant': ariaActiveDescendant,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);
  const inputRef = useRef<HTMLBsdsInputElement>(null);

  // Determine error message
  const getErrorMessage = () => {
    // Ensure value is a string before processing
    const stringValue = typeof value === 'string' ? value : String(value || '');
    const cleanZipCodeValue = stringValue.replace(/\D/g, '');

    if (hasBeenTouched && cleanZipCodeValue.length === 8 && !isValid) {
      return 'CEP inválido';
    }

    if (hasBeenTouched && required && cleanZipCodeValue.length === 0) {
      return 'CEP é obrigatório';
    }

    return '';
  };

  const errorMessage = getErrorMessage();

  // Function to update validation classes on the BsdsInput element
  const updateValidationClasses = useCallback(() => {
    if (inputRef.current) {
      const element = inputRef.current;

      // Add bsds-touched class when user has interacted
      if (hasBeenTouched) {
        element.classList.add('bsds-touched');
      } else {
        element.classList.remove('bsds-touched');
      }

      // Add bsds-invalid class when there's an error
      if (hasBeenTouched && !isValid && errorMessage) {
        element.classList.add('bsds-invalid');
      } else {
        element.classList.remove('bsds-invalid');
      }
    }
  }, [hasBeenTouched, isValid, errorMessage]);

  // Handle input change with formatting (onChange event - blur)
  const handleChange = useCallback(
    (event: React.FormEvent<HTMLBsdsInputElement>) => {
      const target = event.target as HTMLInputElement;
      const newValue = (event as BsdsInputEvent).detail?.value || target?.value || event;
      // Ensure newValue is a string before formatting
      const stringValue = typeof newValue === 'string' ? newValue : String(newValue || '');
      // Apply ZIP Code formatting
      const formattedValue = formatZipCode(stringValue);
      onChange(formattedValue);
      setHasBeenTouched(true);
    },
    [onChange]
  );

  // Handle input change with formatting (onInput event - real time)
  const handleInput = useCallback(
    (event: React.FormEvent<HTMLBsdsInputElement>) => {
      const target = event.target as HTMLInputElement;
      const newValue = (event as BsdsInputEvent).detail?.value || target?.value || event;
      // Ensure newValue is a string before formatting
      const stringValue = typeof newValue === 'string' ? newValue : String(newValue || '');
      // Apply ZIP Code formatting
      const formattedValue = formatZipCode(stringValue);
      onChange(formattedValue);
      setHasBeenTouched(true);
    },
    [onChange]
  );

  // Validate ZIP Code when value changes
  useEffect(() => {
    // Ensure value is a string before processing
    const stringValue = typeof value === 'string' ? value : String(value || '');
    const cleanZipCodeValue = stringValue.replace(/\D/g, '');

    if (cleanZipCodeValue.length === 8) {
      const valid = validateZipCode(stringValue);
      setIsValid(valid);
      onValidationChange?.(valid);
    } else if (cleanZipCodeValue.length === 0) {
      // Empty value - valid if not required, invalid if required
      const valid = !required;
      setIsValid(valid);
      onValidationChange?.(valid);
    } else {
      // Incomplete ZIP Code - don't show error while typing
      setIsValid(true);
      onValidationChange?.(false);
    }
  }, [value, onValidationChange, required]);

  // Update validation classes when state changes
  useEffect(() => {
    updateValidationClasses();
  }, [updateValidationClasses]);

  return (
    <BsdsInput
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onInput={handleInput}
      placeholder={placeholder}
      label={label}
      required={required}
      disabled={disabled}
      className={className}
      maxlength={9}
      type="text"
      clearInput={false}
      inputMode="numeric"
      errorText={errorMessage}
      hasHintIcon
      color="primary"
      platform="web"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      aria-disabled={ariaDisabled}
      aria-autocomplete={ariaAutocomplete}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActiveDescendant}
      aria-errormessage={ariaErrorMessage}
    />
  );
};

